import * as React from "react";
import { SplashLoader } from "@chotca/ui";

/** Plain animated brand splash. Swap-in/swap-out is instant. */
export function AppBootLoader() {
  return <SplashLoader />;
}

/**
 * Boot splash with a guaranteed minimum on-screen time + graceful fade-out.
 *
 * One continuous `SplashLoader` instance lives from first paint until the gate
 * is ready, so the draw/snap animation never restarts or gets cut off:
 *  - `active` true  → stay fully opaque (auth still resolving / redirecting).
 *  - `active` false → wait out the remaining `minMs`, fade, then unmount.
 */
export function BootSplash({
  active,
  minMs = 1600,
  fadeMs = 420,
}: {
  active: boolean;
  minMs?: number;
  fadeMs?: number;
}) {
  const mountedAt = React.useRef(Date.now());
  const [phase, setPhase] = React.useState<"show" | "fade" | "gone">("show");

  // Once the gate stops blocking, hold until the minimum time elapses, then fade.
  React.useEffect(() => {
    if (active || phase !== "show") return;
    const remaining = Math.max(0, minMs - (Date.now() - mountedAt.current));
    const t = setTimeout(() => setPhase("fade"), remaining);
    return () => clearTimeout(t);
  }, [active, phase, minMs]);

  React.useEffect(() => {
    if (phase !== "fade") return;
    const t = setTimeout(() => setPhase("gone"), fadeMs);
    return () => clearTimeout(t);
  }, [phase, fadeMs]);

  // If something starts blocking again mid-fade, keep the splash up.
  React.useEffect(() => {
    if (active && phase === "fade") setPhase("show");
  }, [active, phase]);

  if (phase === "gone") return null;
  return (
    <div
      aria-hidden={phase === "fade"}
      className={
        "fixed inset-0 z-[100] transition-opacity ease-out" +
        (phase === "fade" ? " pointer-events-none" : "")
      }
      style={{
        opacity: phase === "fade" ? 0 : 1,
        transitionDuration: `${fadeMs}ms`,
      }}
    >
      <SplashLoader />
    </div>
  );
}
